import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getConversations,
  deleteConversation,
  renameConversation,
  ConversationSummary,
  ConversationMessage as BackendMessage,
} from '@/api/conversationService'
import { Message } from '@/components/chat/ChatInterface'
import { CitationSource, Citation } from '@/api/chatService'

/** Query key for the conversation list */
export const CONVERSATIONS_KEY = ['conversations'] as const

/**
 * Fetch the conversation list for the sidebar.
 */
export function useConversations() {
  return useQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: () => getConversations(),
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
  })
}

/**
 * Delete a conversation with optimistic cache update.
 */
export function useDeleteConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (conversationId: number) => deleteConversation(conversationId),
    onSuccess: (_data, conversationId) => {
      queryClient.setQueryData<ConversationSummary[]>(
        CONVERSATIONS_KEY,
        (old) => old?.filter((c) => c.conversation_id !== conversationId),
      )
    },
  })
}

/**
 * Rename a conversation with optimistic cache update.
 */
export function useRenameConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ conversationId, title }: { conversationId: number; title: string }) =>
      renameConversation(conversationId, title),
    onSuccess: (data) => {
      queryClient.setQueryData<ConversationSummary[]>(
        CONVERSATIONS_KEY,
        (old) =>
          old?.map((c) =>
            c.conversation_id === data.conversation_id
              ? { ...c, title: data.title }
              : c,
          ),
      )
    },
  })
}

/**
 * Convert backend ConversationMessage pairs (user_input + bot_response)
 * into the frontend's individual Message[] format.
 */
export function convertBackendMessages(backendMessages: BackendMessage[]): Message[] {
  const messages: Message[] = []

  for (const msg of backendMessages) {
    messages.push({
      id: `${msg.id}-user`,
      role: 'user',
      content: msg.user_input,
      timestamp: new Date(msg.created_at),
    })

    if (msg.bot_response) {
      let sources: CitationSource[] | null = null
      let citations: Citation[] | null = null

      try {
        const tmp = document.createElement('div')
        tmp.innerHTML = msg.bot_response
        const el = tmp.querySelector('.nim-citation-data')
        if (el) {
          const raw = el.getAttribute('data-citations')?.replace(/&quot;/g, '"')
          if (raw) {
            const parsed = JSON.parse(raw)
            sources = parsed.sources ?? null
            citations = parsed.citations ?? null
          }
        }
      } catch { /* malformed or missing — degrade gracefully */ }

      messages.push({
        id: msg.id.toString(),
        role: 'assistant',
        content: msg.bot_response,
        timestamp: new Date(msg.created_at),
        feedbackType: msg.feedback_type,
        sources,
        citations,
      })
    }
  }

  return messages
}
